import React from 'react';
import { Button, Icon } from '@gumtree/ui-library';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import { useTheme } from '@emotion/react';
import { trackGA4AndNavigate, trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { getDomainFromUrl } from '@gumtree/shared/src/util/url-service';
import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import { SyiEntrySource, withSyiSource } from '@gumtree/shared/src/util/syi-entry-source';
import { notifyHomePostBannerHeaderClick } from '@gumtree/shared/src/util/home-post-banner-events';
import { NavPostAdButton, sellMyCarButtonCss } from './style';
import { iconButtonCss, IconLabel } from './mhh-nav-bar/mhh-nav-bar.style';

interface PostAdActionProps {
    useMhhLayout: boolean;
    useMhhNavStyle?: boolean;
    useNewIcons: boolean;
    loggedIn: boolean;
    isMobile: boolean;
    pageType: string;
    categoryL2?: string | null;
    buyerUrl: string;
    openDialog: (e: any, type: any, redirect: boolean) => void;
    sellModalType: any;
}

export default function PostAdAction({
    useMhhLayout,
    useMhhNavStyle = false,
    useNewIcons,
    loggedIn,
    isMobile,
    pageType,
    categoryL2,
    buyerUrl,
    openDialog,
    sellModalType,
}: PostAdActionProps) {
    const theme = useTheme();

    const isInCarsVertical =
        ['VIP', 'ResultsSearch', 'ResultsBrowse'].includes(pageType) && categoryL2 === 'cars';

    const handleLoggedInClick = (e: React.MouseEvent) => {
        notifyHomePostBannerHeaderClick();
        const url = withSyiSource('/postad/category', SyiEntrySource.HEADER);
        trackGA4AndNavigate<GA4.ClickPrimaryNavigation>({
            href: url,
            mouseEvent: e,
            payload: {
                event: 'click_primary_navigation',
                linkDomain: getDomainFromUrl(url),
                linkText: 'Post an ad',
                linkURL: url,
                navigationCategory: 'menu',
                navigationHeader: undefined,
                navigationHierarchy: 'header',
                headerCtaClicked: 'new listing',
                pageClickedFrom: pageType,
            },
        });
    };

    const handleNotLoggedInClick = (e: React.MouseEvent) => {
        notifyHomePostBannerHeaderClick();
        openDialog(e, sellModalType, true);
        trackV2('PostAdBegin', 'PostAdHeader');
        trackGA4Event<GA4.ClickPrimaryNavigation>({
            event: 'click_primary_navigation',
            linkText: 'Post an ad',
            navigationCategory: 'menu',
            navigationHeader: undefined,
            navigationHierarchy: 'header',
            headerCtaClicked: 'new listing',
            pageClickedFrom: pageType,
        });
    };

    const handleCarsVerticalClick = (e: React.MouseEvent) => {
        openDialog(e, sellModalType, true);
    };

    const sellMyCarUrl = `${buyerUrl}/sell-my-car`;

    if (useMhhLayout) {
        if (isInCarsVertical && !loggedIn) {
            return (
                <a
                    href={sellMyCarUrl}
                    css={iconButtonCss}
                    {...qaAttribute('post-ad-button')}
                    data-testid="post-ad-button"
                    onClick={handleCarsVerticalClick}
                >
                    <Icon type="navbar-car" />
                    <IconLabel>Sell</IconLabel>
                </a>
            );
        }
        if (loggedIn) {
            return (
                <a
                    href={withSyiSource('/postad/category', SyiEntrySource.HEADER)}
                    css={iconButtonCss}
                    {...qaAttribute('post-ad-button')}
                    data-testid="post-ad-button"
                    onClick={handleLoggedInClick}
                >
                    <Icon type="navbar-post" />
                    <IconLabel>Post an ad</IconLabel>
                </a>
            );
        }
        return (
            <button
                type="button"
                css={iconButtonCss}
                {...qaAttribute('post-ad-button')}
                data-testid="post-ad-button"
                onClick={handleNotLoggedInClick}
            >
                <Icon type="navbar-post" />
                <IconLabel>Post an ad</IconLabel>
            </button>
        );
    }

    if (isInCarsVertical && !loggedIn) {
        return (
            <Button
                dark
                label={isMobile ? 'Sell' : 'Sell my Car'}
                icon={useNewIcons ? <Icon type="navbar-car" /> : <Icon type="car" />}
                bottomLabel
                href={sellMyCarUrl}
                onClick={handleCarsVerticalClick}
                css={sellMyCarButtonCss(theme, useMhhNavStyle)}
            />
        );
    }
    if (!loggedIn) {
        return (
            <NavPostAdButton
                dark
                data-testid="post-ad-button"
                mobileCondition={isMobile}
                mhhStyle={useMhhNavStyle}
                iconName={useNewIcons ? 'navbar-post' : 'sell-outline'}
                label="Post an ad"
                onClick={handleNotLoggedInClick}
                bottomLabel
            />
        );
    }
    return (
        <NavPostAdButton
            dark
            data-testid="post-ad-button"
            mobileCondition={isMobile}
            mhhStyle={useMhhNavStyle}
            {...(useNewIcons ? { iconName: 'navbar-post' } : {})}
            label="Post an ad"
            onClick={handleLoggedInClick}
            bottomLabel
        />
    );
}
